import {useCallback, useState} from 'react';

export interface HoverStateProps {
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onMouseMove: () => void;
}

type UseHoverStateOutput = [boolean, HoverStateProps];

/**
 * Use to track whether an HTML element is currently hovered.
 *
 * Example usage:
 * function Component() {
 *   const [isHovered, hoverProps] = useHoverState();
 *   return <div {...hoverProps}>The div is {isHovered ? '' : 'not'} hovered!</div>;
 * }
 */
export default function useHoverState(): UseHoverStateOutput {
  const [isHovered, setIsHovered] = useState(false);

  const onMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, [setIsHovered]);

  const onMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, [setIsHovered]);

  // Included because in certain situations, the onMouseEnter callback is not triggered.
  // For example, if the user's mouse is over a tooltip overlaid on the component using useHoverState, and
  // the tooltip disappears, the onMouseEnter for the component would not be triggered.
  // See BNCH-21556 description for a gif of this in action.
  const onMouseMove = useCallback(() => {
    setIsHovered(true);
  }, [setIsHovered]);

  return [isHovered, {onMouseEnter, onMouseLeave, onMouseMove}];
}
